import { useState, useEffect, useMemo, useCallback } from 'react';
import { formatEther } from 'ethers/lib/utils';
import { SubscriptionHandler } from 'urql';

import {
  OnReady,
  SearchSymbols,
  ResolveSymbol,
  GetBars,
  SubscribeBars,
  UnsubscribeBars,
  GenericCandle,
} from './types';
import { IBasicDataFeed, ResolutionString } from '../charting_library/charting_library';
import { SubscribeBarsCallback } from '../charting_library/datafeed-api.d';
import dataFeedWrapper from '../utils/dataFeedWrapper';
import {
  useFifteenMinuteCandlesQuery,
  useThirtyMinuteCandlesQuery,
  useHourlyCandlesQuery,
  useQuadHourlyCandlesQuery,
  useDailyCandlesQuery,
  useLiveFifteenMinuteCandlesSubscription,
  useLiveThirtyMinuteCandlesSubscription,
  useLiveHourlyCandlesSubscription,
  useLiveQuadHourlyCandlesSubscription,
  useLiveDailyCandlesSubscription,
  LiveFifteenMinuteCandlesSubscription,
  LiveThirtyMinuteCandlesSubscription,
  LiveHourlyCandlesSubscription,
  LiveQuadHourlyCandlesSubscription,
  LiveDailyCandlesSubscription,
} from '../graphql/generated';
import useMarkets from '../contexts/MarketsContext';

const SUPPORTED_RESOLUTIONS = ['15', '30', '60', '240', '1d'] as ResolutionString[];

interface BarQuery {
  bars: GenericCandle[];
  fetching: boolean;
  earliestBar: number;
}

const formatPrice = (price: string): number => Number(formatEther(price));

const formatBar = (candle: GenericCandle) => ({
  high: formatPrice(candle.high),
  low: formatPrice(candle.low),
  open: formatPrice(candle.open),
  close: formatPrice(candle.close),
  volume: formatPrice(candle.volume),
  time: Number(candle.openTimestamp) * 1000,
});

export const useDatafeed = () => {
  const { selectedMarket, marketList } = useMarkets();
  const [onReady, setOnReady] = useState<OnReady>();
  const [searchSymbols, setSearchSymbols] = useState<SearchSymbols>();
  const [resolveSymbol, setResolveSymbol] = useState<ResolveSymbol>();
  const [getBars, setGetBars] = useState<GetBars>();
  const [sentBars, setSentBars] = useState(false);
  const [onTick, setOnTick] = useState<SubscribeBarsCallback>();

  const [subscribeBars, setSubscribeBars] = useState<SubscribeBars>();
  const [unsubscribeBars, setUnsubscribeBars] = useState<UnsubscribeBars>();

  const getBarsQueryParams = useCallback(
    (resolution: ResolutionString) => ({
      variables: {
        id: getBars?.symbolInfo?.ticker ?? '',
        to: getBars?.periodParams?.to,
        from: getBars?.periodParams?.from,
        countBack: getBars?.periodParams?.countBack,
      },
      pause:
        !getBars?.periodParams?.countBack ||
        !getBars?.periodParams?.to ||
        !getBars?.symbolInfo?.ticker ||
        getBars?.resolution !== resolution,
    }),
    [getBars],
  );

  const getBarsSubParams = useCallback(
    (resolution: ResolutionString) => ({
      variables: {
        id: subscribeBars?.symbolInfo?.ticker ?? '',
      },
      pause: !subscribeBars?.symbolInfo?.ticker || subscribeBars?.resolution !== resolution,
    }),
    [subscribeBars],
  );

  const fifteenMinuteQuery = useFifteenMinuteCandlesQuery(
    getBarsQueryParams(SUPPORTED_RESOLUTIONS[0]),
  );
  const thirtyMinuteQuery = useThirtyMinuteCandlesQuery(
    getBarsQueryParams(SUPPORTED_RESOLUTIONS[1]),
  );
  const hourlyQuery = useHourlyCandlesQuery(getBarsQueryParams(SUPPORTED_RESOLUTIONS[2]));
  const quadHourlyQuery = useQuadHourlyCandlesQuery(getBarsQueryParams(SUPPORTED_RESOLUTIONS[3]));
  const dailyQuery = useDailyCandlesQuery(getBarsQueryParams(SUPPORTED_RESOLUTIONS[4]));

  const handleLiveFifteenMinuteCandlesSubscription = useCallback<
    SubscriptionHandler<LiveFifteenMinuteCandlesSubscription, LiveFifteenMinuteCandlesSubscription>
  >(
    (
      prev: LiveFifteenMinuteCandlesSubscription | undefined,
      result: LiveFifteenMinuteCandlesSubscription,
    ) => {
      const lastLatestCandle = prev?.market?.fifteenMinuteCandles?.sort(
        (curr, next) => Number(next.openTimestamp) - Number(curr.openTimestamp),
      )[0];
      const combined = {
        ...prev,
        market: {
          ...(prev?.market ?? {}),
          earliestPrice: result?.market?.earliestPrice ?? prev?.market?.earliestPrice,
          fifteenMinuteCandles: [
            ...(prev?.market?.fifteenMinuteCandles ?? []),
            ...(result?.market?.fifteenMinuteCandles ?? []),
          ],
        },
      };
      const latestCandle =
        combined.market.fifteenMinuteCandles[combined.market.fifteenMinuteCandles.length - 1];
      if (
        onTick &&
        latestCandle &&
        lastLatestCandle &&
        lastLatestCandle.openTimestamp < latestCandle.openTimestamp
      ) {
        onTick(formatBar(latestCandle as GenericCandle));
      }
      return combined;
    },
    [onTick],
  );
  const handleLiveThirtyMinuteCandlesSubscription = useCallback<
    SubscriptionHandler<LiveThirtyMinuteCandlesSubscription, LiveThirtyMinuteCandlesSubscription>
  >(
    (
      prev: LiveThirtyMinuteCandlesSubscription | undefined,
      result: LiveThirtyMinuteCandlesSubscription,
    ) => {
      const lastLatestCandle = prev?.market?.thirtyMinuteCandles?.sort(
        (curr, next) => Number(next.openTimestamp) - Number(curr.openTimestamp),
      )[0];
      const combined = {
        ...prev,
        market: {
          ...(prev?.market ?? {}),
          earliestPrice: result?.market?.earliestPrice ?? prev?.market?.earliestPrice,
          thirtyMinuteCandles: [
            ...(prev?.market?.thirtyMinuteCandles ?? []),
            ...(result?.market?.thirtyMinuteCandles ?? []),
          ],
        },
      };
      const latestCandle =
        combined.market.thirtyMinuteCandles[combined.market.thirtyMinuteCandles.length - 1];
      if (
        onTick &&
        latestCandle &&
        lastLatestCandle &&
        lastLatestCandle.openTimestamp < latestCandle.openTimestamp
      ) {
        onTick(formatBar(latestCandle as GenericCandle));
      }
      return combined;
    },
    [onTick],
  );
  const handleLiveHourlyCandlesSubscription = useCallback<
    SubscriptionHandler<LiveHourlyCandlesSubscription, LiveHourlyCandlesSubscription>
  >(
    (prev: LiveHourlyCandlesSubscription | undefined, result: LiveHourlyCandlesSubscription) => {
      const lastLatestCandle = prev?.market?.hourlyCandles?.sort(
        (curr, next) => Number(next.openTimestamp) - Number(curr.openTimestamp),
      )[0];
      const combined = {
        ...prev,
        market: {
          ...(prev?.market ?? {}),
          earliestPrice: result?.market?.earliestPrice ?? prev?.market?.earliestPrice,
          hourlyCandles: [
            ...(prev?.market?.hourlyCandles ?? []),
            ...(result?.market?.hourlyCandles ?? []),
          ],
        },
      };
      const latestCandle = combined.market.hourlyCandles[combined.market.hourlyCandles.length - 1];
      if (
        onTick &&
        latestCandle &&
        lastLatestCandle &&
        lastLatestCandle.openTimestamp < latestCandle.openTimestamp
      ) {
        onTick(formatBar(latestCandle as GenericCandle));
      }
      return combined;
    },
    [onTick],
  );
  const handleLiveQuadHourlyCandlesSubscription = useCallback<
    SubscriptionHandler<LiveQuadHourlyCandlesSubscription, LiveQuadHourlyCandlesSubscription>
  >(
    (
      prev: LiveQuadHourlyCandlesSubscription | undefined,
      result: LiveQuadHourlyCandlesSubscription,
    ) => {
      const lastLatestCandle = prev?.market?.quadHourlyCandles?.sort(
        (curr, next) => Number(next.openTimestamp) - Number(curr.openTimestamp),
      )[0];
      const combined = {
        ...prev,
        market: {
          ...(prev?.market ?? {}),
          earliestPrice: result?.market?.earliestPrice ?? prev?.market?.earliestPrice,
          quadHourlyCandles: [
            ...(prev?.market?.quadHourlyCandles ?? []),
            ...(result?.market?.quadHourlyCandles ?? []),
          ],
        },
      };
      const latestCandle =
        combined.market.quadHourlyCandles[combined.market.quadHourlyCandles.length - 1];
      if (
        onTick &&
        latestCandle &&
        lastLatestCandle &&
        lastLatestCandle.openTimestamp < latestCandle.openTimestamp
      ) {
        onTick(formatBar(latestCandle as GenericCandle));
      }
      return combined;
    },
    [onTick],
  );
  const handleLiveDailyCandlesSubscription = useCallback<
    SubscriptionHandler<LiveDailyCandlesSubscription, LiveDailyCandlesSubscription>
  >(
    (prev: LiveDailyCandlesSubscription | undefined, result: LiveDailyCandlesSubscription) => {
      const lastLatestCandle = prev?.market?.dailyCandles?.sort(
        (curr, next) => Number(next.openTimestamp) - Number(curr.openTimestamp),
      )[0];
      const combined = {
        ...prev,
        market: {
          ...(prev?.market ?? {}),
          earliestPrice: result?.market?.earliestPrice ?? prev?.market?.earliestPrice,
          dailyCandles: [
            ...(prev?.market?.dailyCandles ?? []),
            ...(result?.market?.dailyCandles ?? []),
          ],
        },
      };
      const latestCandle = combined.market.dailyCandles[combined.market.dailyCandles.length - 1];
      if (
        onTick &&
        latestCandle &&
        lastLatestCandle &&
        lastLatestCandle.openTimestamp < latestCandle.openTimestamp
      ) {
        onTick(formatBar(latestCandle as GenericCandle));
      }
      return combined;
    },
    [onTick],
  );

  useLiveFifteenMinuteCandlesSubscription(
    getBarsSubParams(SUPPORTED_RESOLUTIONS[0]),
    handleLiveFifteenMinuteCandlesSubscription,
  );
  useLiveThirtyMinuteCandlesSubscription(
    getBarsSubParams(SUPPORTED_RESOLUTIONS[1]),
    handleLiveThirtyMinuteCandlesSubscription,
  );
  useLiveHourlyCandlesSubscription(
    getBarsSubParams(SUPPORTED_RESOLUTIONS[2]),
    handleLiveHourlyCandlesSubscription,
  );
  useLiveQuadHourlyCandlesSubscription(
    getBarsSubParams(SUPPORTED_RESOLUTIONS[3]),
    handleLiveQuadHourlyCandlesSubscription,
  );
  useLiveDailyCandlesSubscription(
    getBarsSubParams(SUPPORTED_RESOLUTIONS[4]),
    handleLiveDailyCandlesSubscription,
  );

  // Return the query corresponding to the current resolution
  const barsQuery = useMemo<BarQuery>(() => {
    switch (getBars?.resolution) {
      case SUPPORTED_RESOLUTIONS[0]:
        return {
          bars: fifteenMinuteQuery[0].data?.market?.fifteenMinuteCandles as GenericCandle[],
          fetching: fifteenMinuteQuery[0].fetching,
          earliestBar: fifteenMinuteQuery[0].data?.market?.earliestPrice,
        };
      case SUPPORTED_RESOLUTIONS[1]:
        return {
          bars: thirtyMinuteQuery[0].data?.market?.thirtyMinuteCandles as GenericCandle[],
          fetching: thirtyMinuteQuery[0].fetching,
          earliestBar: thirtyMinuteQuery[0].data?.market?.earliestPrice,
        };
      case SUPPORTED_RESOLUTIONS[2]:
        return {
          bars: hourlyQuery[0].data?.market?.hourlyCandles as GenericCandle[],
          fetching: hourlyQuery[0].fetching,
          earliestBar: hourlyQuery[0].data?.market?.earliestPrice,
        };
      case SUPPORTED_RESOLUTIONS[3]:
        return {
          bars: quadHourlyQuery[0].data?.market?.quadHourlyCandles as GenericCandle[],
          fetching: quadHourlyQuery[0].fetching,
          earliestBar: quadHourlyQuery[0].data?.market?.earliestPrice,
        };
      case SUPPORTED_RESOLUTIONS[4]:
        return {
          bars: dailyQuery[0].data?.market?.dailyCandles as GenericCandle[],
          fetching: dailyQuery[0].fetching,
          earliestBar: dailyQuery[0].data?.market?.earliestPrice,
        };
      default:
        return {
          bars: dailyQuery[0].data?.market?.dailyCandles as GenericCandle[],
          fetching: dailyQuery[0].fetching,
          earliestBar: dailyQuery[0].data?.market?.earliestPrice,
        };
    }
  }, [
    getBars?.resolution,
    fifteenMinuteQuery,
    thirtyMinuteQuery,
    hourlyQuery,
    quadHourlyQuery,
    dailyQuery,
  ]);

  const dataFeed = useMemo<IBasicDataFeed>(() => {
    return dataFeedWrapper({
      setOnReady,
      setSearchSymbols,
      setResolveSymbol,
      setGetBars,
      setSubscribeBars,
      setUnsubscribeBars,
    });
  }, [
    setOnReady,
    setSearchSymbols,
    setResolveSymbol,
    setGetBars,
    setSubscribeBars,
    setUnsubscribeBars,
  ]);

  useEffect(() => {
    if (marketList && onReady?.callback) {
      onReady.callback({
        exchanges: marketList.map(({ id, symbol, name }) => ({
          value: id,
          name: symbol,
          desc: name,
        })),
        supported_resolutions: SUPPORTED_RESOLUTIONS,
      });
    }
  }, [marketList, onReady]);

  useEffect(() => {
    if (selectedMarket && searchSymbols) {
      console.log('Unused method called: searchSymbols', searchSymbols);
    }
  }, [searchSymbols, selectedMarket]);

  useEffect(() => {
    if (marketList && resolveSymbol?.symbolName) {
      const symbolInfo = marketList.find((market) => market.id === resolveSymbol.symbolName);
      if (symbolInfo) {
        resolveSymbol.onResolve({
          name: symbolInfo.symbol,
          full_name: symbolInfo.name,
          ticker: symbolInfo.id,
          description: `${symbolInfo.name} Increment Perpetual Market`,
          type: 'crypto',
          session: '24x7',
          session_display: '24/7',
          exchange: 'zkSync era',
          listed_exchange: 'zkSync era',
          timezone: 'Etc/UTC',
          format: 'price',
          minmov: 1,
          pricescale: 10 ** symbolInfo.displayDecimals,
          has_intraday: true,
          has_empty_bars: true,
          supported_resolutions: SUPPORTED_RESOLUTIONS,
        });
      }
    }
  }, [resolveSymbol, marketList]);

  useEffect(() => {
    if (getBars && barsQuery.bars && !sentBars && !barsQuery.fetching) {
      const bars = barsQuery.bars.map(formatBar);
      const noData =
        Number(barsQuery.earliestBar) > getBars.periodParams.from || getBars.periodParams.from < 0;
      getBars.onResult(bars, {
        noData,
      });
      setSentBars(true);
    }
  }, [getBars, barsQuery, sentBars]);

  useEffect(() => {
    if (getBars) {
      setSentBars(false);
    }
  }, [getBars, barsQuery]);

  useEffect(() => {
    if (selectedMarket && subscribeBars?.symbolInfo && subscribeBars?.onTick) {
      setOnTick(() => subscribeBars.onTick);
    }
  }, [subscribeBars, selectedMarket, onTick]);

  useEffect(() => {
    if (selectedMarket && unsubscribeBars?.listenerGuid) {
      console.log('Unused method called: unsubscribeBars', unsubscribeBars);
    }
  }, [unsubscribeBars, selectedMarket]);

  return dataFeed;
};
